import { Transforms, Editor, Path, Range } from 'slate';
import type { SlateBlock, ImageBlock } from '@atlas/renderer';
import createTextBlock from '@atlas/tool/box/slate/createTextBlock';
import getNode from '@atlas/tool/box/slate/getNode';
import isVoidBlock from '@atlas/tool/box/slate/isVoidBlock';
import isTextBlock from '@atlas/tool/box/slate/isTextBlock';
import isEmptyContent from '@atlas/tool/box/slate/isEmptyContent';
import deleteSelection from '@atlas/tool/box/slate/deleteSelection';
import isTableBlock from '@atlas/tool/box/slate/isTableBlock';
import isListBlock from '@atlas/tool/box/slate/isListBlock';

/**
 * Inserts image node into the editor and moves cursor to the following text block
 */
export default function insertImageNode(
    editor: Editor,
    options: { src?: string; fingerprint?: string; width?: number; align?: ImageBlock['align'] },
) {
    const image: ImageBlock = {
        type: 'image',
        src: options.src ?? '',
        fingerprint: options.fingerprint,
        width: options.width,
        align: options.align,
        children: [{ text: '' }],
    };

    if (!editor.selection) {
        // If we don't know what's going on — add image and empty text line after
        Transforms.insertNodes(editor, [image, createTextBlock()], { mode: 'highest' });
        return;
    }

    if (!Range.isCollapsed(editor.selection)) {
        deleteSelection(editor, editor.selection);
    }

    let currentTopPath = editor.selection.focus.path.slice(0, 1);
    let node = getNode<SlateBlock>(editor, currentTopPath);

    if (node && (isTableBlock(node) || isListBlock(node))) {
        currentTopPath = [currentTopPath[0] + 1];
        node = createTextBlock();
        Transforms.insertNodes(editor, node, { select: true, mode: 'highest' });
    }

    Transforms.insertNodes(editor, image, { mode: 'highest' });

    if (node && isTextBlock(node) && isEmptyContent(node.children)) {
        // If empty text line left at the place where image was inserted — get rid of it, as it was replaced by the image
        Transforms.removeNodes(editor, { at: currentTopPath });
    }

    // Now we need to put cursor in the next text block (add one if it doesn't exist)
    const nextPath = Path.next(editor.selection.focus.path.slice(0, 1));
    const block = getNode<SlateBlock>(editor, nextPath.slice(0, 1));
    if (!block || isVoidBlock(block)) {
        Transforms.insertNodes(editor, createTextBlock(), { select: true, mode: 'highest' });
    } else {
        Transforms.select(editor, Editor.start(editor, nextPath));
    }
}
